import { useState, useEffect } from 'react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import PhoneInput, { PAISES, validarNumero } from '@/components/PhoneInput'
import type { PaisOpcao } from '@/components/PhoneInput'
import BlocoApresentacaoConsultora from '@/components/BlocoApresentacaoConsultora'

// ═══════════════════════════════════════
// TIPOS
// ═══════════════════════════════════════

type FaixaEtaria = 'BEBE' | 'PRE_ESCOLAR' | 'CRIANCA' | 'ADOLESCENTE' | ''

interface DadosBasicos {
  nomeMae: string
  whatsapp: string
  paisOpcao: PaisOpcao
  nomeKid: string
  idadeKid: string
}

// ═══════════════════════════════════════
// PERGUNTAS POR FAIXA ETÁRIA
// Telas com seleção de ATÉ 2 opções:
// sono, humor, emocional, foco,
// comportamento, saude_fisica,
// imunidade, socializacao
// ═══════════════════════════════════════

const perguntasBebe = [
  {
    id: 'sono',
    titulo: 'Sono',
    pergunta: 'Como está o sono de [nome]?',
    multipla: true,
    opcoes: [
      'Dorme bem e acorda tranquilo(a)',
      'Tem dificuldade para adormecer',
      'Acorda várias vezes à noite',
      'Chora muito durante a noite',
      'O sono é agitado e inquieto',
    ],
  },
  {
    id: 'humor',
    titulo: 'Humor e temperamento',
    pergunta: 'Como está o humor de [nome] no dia a dia?',
    multipla: true,
    opcoes: [
      'Tranquilo(a) e feliz',
      'Muito agitado(a) e choroso(a)',
      'Irritado(a) e difícil de acalmar',
      'Assustado(a) e muito sensível',
      'Alternado entre calmo e agitado',
    ],
  },
  {
    id: 'saude_fisica',
    titulo: 'Saúde física',
    pergunta: 'Como está a saúde física de [nome]?',
    multipla: true,
    opcoes: [
      'Saudável e raramente fica doente',
      'Fica doente com frequência',
      'Tem problemas respiratórios (tosse, bronquite, congestão)',
      'Tem cólicas e desconforto abdominal frequente',
      'Tem problemas de pele (assaduras, irritações, eczema)',
      'Tem refluxo ou regurgitação frequente',
    ],
  },
  {
    id: 'imunidade',
    titulo: 'Imunidade',
    pergunta: 'Como está a imunidade de [nome]?',
    multipla: true,
    opcoes: [
      'Forte e raramente adoece',
      'Adoece com frequência',
      'Demora muito para se recuperar',
      'Tem alergias recorrentes',
      'Tem infecções frequentes (ouvido, garganta, vias aéreas)',
    ],
  },
]

const perguntasPreEscolar = [
  {
    id: 'sono',
    titulo: 'Sono',
    pergunta: 'Como está o sono de [nome]?',
    multipla: true,
    opcoes: [
      'Dorme bem e acorda descansado(a)',
      'Resiste muito na hora de dormir',
      'Tem pesadelos frequentes',
      'Acorda várias vezes à noite',
      'O sono é agitado e inquieto',
      'Tem medo do escuro ou de ficar sozinho(a)',
    ],
  },
  {
    id: 'emocional',
    titulo: 'Equilíbrio emocional',
    pergunta: 'Como está o equilíbrio emocional de [nome]?',
    multipla: true,
    opcoes: [
      'Equilibrado(a) e tranquilo(a)',
      'Com birras e explosões frequentes',
      'Ansioso(a) e com medos',
      'Triste e retraído(a)',
      'Agitado(a) e difícil de acalmar',
      'Muito sensível e chora com facilidade',
    ],
  },
  {
    id: 'comportamento',
    titulo: 'Comportamento',
    pergunta: 'Como é o comportamento de [nome] no dia a dia?',
    multipla: true,
    opcoes: [
      'Cooperativo(a) e tranquilo(a)',
      'Agitado(a) e hiperativo(a)',
      'Tímido(a) e com dificuldade de socializar',
      'Muito apegado(a) e com dificuldade de separação',
      'Desobediente e sem limites',
      'Com dificuldade de adaptação a mudanças e novidades',
    ],
  },
  {
    id: 'saude_fisica',
    titulo: 'Saúde física',
    pergunta: 'Como está a saúde física de [nome]?',
    multipla: true,
    opcoes: [
      'Saudável e raramente fica doente',
      'Fica doente com frequência',
      'Tem problemas respiratórios recorrentes (tosse, bronquite, asma, rinite)',
      'Tem problemas digestivos frequentes (gases, constipação, dor de barriga, náusea)',
      'Tem dores de crescimento',
      'Tem problemas de pele (irritações, eczema, alergias)',
    ],
  },
  {
    id: 'imunidade',
    titulo: 'Imunidade',
    pergunta: 'Como está a imunidade de [nome]?',
    multipla: true,
    opcoes: [
      'Forte e raramente adoece',
      'Adoece com frequência',
      'Demora muito para se recuperar',
      'Tem alergias recorrentes',
      'Tem infecções frequentes (ouvido, garganta, vias aéreas)',
    ],
  },
]

const perguntasCrianca = [
  {
    id: 'sono',
    titulo: 'Sono',
    pergunta: 'Como está o sono de [nome]?',
    multipla: true,
    opcoes: [
      'Dorme bem e acorda descansado(a)',
      'Tem dificuldade para adormecer',
      'Acorda várias vezes à noite',
      'Tem pesadelos frequentes',
      'Resiste muito na hora de dormir',
      'Acorda cansado(a) mesmo após uma noite de sono',
    ],
  },
  {
    id: 'foco',
    titulo: 'Foco e concentração',
    pergunta: 'Como está o foco de [nome] na escola e nas atividades?',
    multipla: true,
    opcoes: [
      'Focado(a) e atento(a)',
      'Disperso(a) e desorganizado(a)',
      'Hiperativo(a) e agitado(a)',
      'Com dificuldade de aprendizagem',
      'Começa as tarefas mas não termina',
      'Diagnóstico de TDAH ou similar',
    ],
  },
  {
    id: 'emocional',
    titulo: 'Equilíbrio emocional',
    pergunta: 'Como está o equilíbrio emocional de [nome]?',
    multipla: true,
    opcoes: [
      'Equilibrado(a) e tranquilo(a)',
      'Irritado(a) e com explosões de raiva',
      'Ansioso(a) e nervoso(a)',
      'Triste e isolado(a)',
      'Agitado(a) e sem limites',
      'Com medos e inseguranças',
      'Muito sensível e chora com facilidade',
    ],
  },
  {
    id: 'saude_fisica',
    titulo: 'Saúde física',
    pergunta: 'Como está a saúde física de [nome]?',
    multipla: true,
    opcoes: [
      'Saudável e raramente fica doente',
      'Fica doente com frequência',
      'Tem problemas respiratórios recorrentes (tosse, bronquite, asma, rinite)',
      'Tem problemas digestivos frequentes (gases, constipação, dor de barriga, náusea)',
      'Tem dores de crescimento frequentes',
      'Tem dores de cabeça frequentes',
      'Tem problemas de pele (acne, irritações, eczema)',
    ],
  },
  {
    id: 'imunidade',
    titulo: 'Imunidade',
    pergunta: 'Como está a imunidade de [nome]?',
    multipla: true,
    opcoes: [
      'Forte e raramente adoece',
      'Adoece com frequência',
      'Demora muito para se recuperar',
      'Tem alergias recorrentes',
      'Tem infecções frequentes (ouvido, garganta, vias aéreas)',
    ],
  },
  {
    id: 'socializacao',
    titulo: 'Comportamento e socialização',
    pergunta: 'Como está o comportamento e a socialização de [nome]?',
    multipla: true,
    opcoes: [
      'Sociável e bem adaptado(a)',
      'Tímido(a) com dificuldade de fazer amigos',
      'Com conflitos frequentes com colegas e familiares',
      'Muito retraído(a) e isolado(a)',
      'Agressivo(a) com os outros',
      'Com baixa autoestima e insegurança',
    ],
  },
]

const perguntasAdolescente = [
  {
    id: 'sono',
    titulo: 'Sono',
    pergunta: 'Como está o sono de [nome]?',
    multipla: true,
    opcoes: [
      'Dorme bem e acorda descansado(a)',
      'Tem dificuldade para adormecer',
      'Dorme muito mas não descansa',
      'Acorda várias vezes à noite',
      'Tem insônia frequente',
      'O sono é muito irregular',
    ],
  },
  {
    id: 'foco',
    titulo: 'Foco e desempenho',
    pergunta: 'Como está o foco e desempenho de [nome] nos estudos?',
    multipla: true,
    opcoes: [
      'Focado(a) e produtivo(a)',
      'Disperso(a) e desorganizado(a)',
      'Com dificuldade de concentração',
      'Procrastina muito as tarefas',
      'Com queda no desempenho escolar',
      'Diagnóstico de TDAH ou similar',
    ],
  },
  {
    id: 'emocional',
    titulo: 'Equilíbrio emocional',
    pergunta: 'Como está o equilíbrio emocional de [nome]?',
    multipla: true,
    opcoes: [
      'Equilibrado(a) e bem consigo mesmo(a)',
      'Ansioso(a) e nervoso(a)',
      'Irritado(a) e impaciente',
      'Triste e isolado(a)',
      'Inseguro(a) e com baixa autoestima',
      'Com mudanças de humor frequentes',
      'Com crises de choro sem motivo aparente',
    ],
  },
  {
    id: 'saude_fisica',
    titulo: 'Saúde física',
    pergunta: 'Como está a saúde física de [nome]?',
    multipla: true,
    opcoes: [
      'Saudável e raramente fica doente',
      'Fica doente com frequência',
      'Tem problemas respiratórios recorrentes (tosse, bronquite, asma, rinite)',
      'Tem problemas digestivos frequentes (gases, constipação, dor de barriga, náusea)',
      'Tem dores de cabeça frequentes',
      'Tem problemas de pele (acne, oleosidade, irritações)',
      'Tem dores musculares frequentes',
    ],
  },
  {
    id: 'imunidade',
    titulo: 'Imunidade',
    pergunta: 'Como está a imunidade de [nome]?',
    multipla: true,
    opcoes: [
      'Forte e raramente adoece',
      'Adoece com frequência',
      'Demora muito para se recuperar',
      'Tem alergias recorrentes',
      'Tem infecções frequentes (garganta, vias aéreas)',
    ],
  },
  {
    id: 'socializacao',
    titulo: 'Comportamento e socialização',
    pergunta: 'Como está o comportamento e a socialização de [nome]?',
    multipla: true,
    opcoes: [
      'Sociável e bem adaptado(a)',
      'Muito retraído(a) e isolado(a)',
      'Com conflitos frequentes em casa e na escola',
      'Com dificuldade de fazer e manter amizades',
      'Muito influenciável pelo grupo de amigos',
      'Com comportamentos de risco ou autolesão',
    ],
  },
]

// ═══════════════════════════════════════
// DEMANDAS ESPECIAIS POR FAIXA
// ═══════════════════════════════════════

const demandasBebe = [
  'Síndrome de Down', 'Deficiência intelectual', 'Deficiência física', 'Deficiência auditiva',
  'Deficiência visual', 'Autismo (suspeita ou diagnóstico)', 'Atraso no desenvolvimento',
  'Alergias ou intolerâncias', 'Doença autoimune', 'Refluxo severo', 'Cólicas intensas', 'Nenhuma das anteriores',
]

const demandasPreEscolar = [
  'Autismo (TEA)', 'Síndrome de Down', 'Deficiência intelectual', 'Deficiência física', 'Deficiência auditiva',
  'Deficiência visual', 'Atraso no desenvolvimento', 'Ansiedade diagnosticada',
  'Alergias ou intolerâncias', 'Doença autoimune', 'Nenhuma das anteriores',
]

const demandasCrianca = [
  'TDAH ou DDA', 'Autismo (TEA)', 'Hiperatividade', 'Déficit de atenção',
  'Síndrome de Down', 'Deficiência intelectual', 'Deficiência física',
  'Deficiência auditiva', 'Deficiência visual', 'Ansiedade diagnosticada', 'Dislexia ou dificuldade de aprendizagem',
  'Alergias ou intolerâncias', 'Doença autoimune', 'Nenhuma das anteriores',
]

const demandasAdolescente = [
  'TDAH ou DDA', 'Autismo (TEA)', 'Hiperatividade', 'Déficit de atenção',
  'Síndrome de Down', 'Deficiência intelectual', 'Deficiência física',
  'Deficiência auditiva', 'Deficiência visual', 'Ansiedade diagnosticada', 'Depressão diagnosticada',
  'Dislexia ou dificuldade de aprendizagem', 'Alergias ou intolerâncias', 'Doença autoimune',
  'Síndrome do pânico', 'Nenhuma das anteriores',
]

// ═══════════════════════════════════════
// PRIORIDADES POR FAIXA
// ═══════════════════════════════════════

const prioridadesBebe = [
  'Qualidade do sono', 'Calma e tranquilidade', 'Conforto digestivo',
  'Imunidade e proteção', 'Saúde respiratória', 'Conforto e alívio de desconfortos', 'Saúde da pele',
]

const prioridadesPreEscolar = [
  'Qualidade do sono', 'Equilíbrio emocional e calma', 'Comportamento e limites',
  'Coragem e autoconfiança', 'Imunidade e proteção', 'Saúde respiratória',
  'Digestão e conforto abdominal', 'Alívio de dores',
]

const prioridadesCrianca = [
  'Qualidade do sono', 'Foco e concentração', 'Equilíbrio emocional',
  'Coragem e autoconfiança', 'Imunidade e proteção', 'Saúde respiratória',
  'Digestão e conforto abdominal', 'Alívio de dores', 'Socialização e comportamento',
]

const prioridadesAdolescente = [
  'Qualidade do sono', 'Foco e concentração', 'Equilíbrio emocional',
  'Autoestima e autoconfiança', 'Imunidade e proteção', 'Saúde respiratória',
  'Digestão e conforto abdominal', 'Saúde da pele', 'Alívio de dores e tensão', 'Socialização e comportamento',
]

// ═══════════════════════════════════════
// HELPER: DEMANDAS QUE PERMITEM VETIVER
// ═══════════════════════════════════════

function demandasPermitiamVetiver(demandas: string[]): boolean {
  return demandas.some(d =>
    d.includes('TDAH') || d.includes('DDA') ||
    d.includes('Autismo') || d.includes('TEA') ||
    d.includes('Hiperatividade') ||
    d.includes('Déficit de atenção')
  )
}

// ═══════════════════════════════════════
// LÓGICA DE INDICAÇÃO DE MIXES
// ═══════════════════════════════════════

function calcularMixes(respostas: Record<string, string[]>, demandas: string[], prioridades: string[], faixa: FaixaEtaria) {
  const mixes = new Set<string>()
  const touch = new Set<string>()
  const difusor = new Set<string>()

  const temResposta = (tela: string, ...termos: string[]) => {
    const resp = respostas[tela] || []
    return termos.some(t => resp.some(r => r.toLowerCase().includes(t.toLowerCase())))
  }

  // SONO
  if (temResposta('sono', 'dificuldade', 'acorda', 'agitado', 'noite', 'chora', 'insônia', 'irregular', 'pesadelos', 'resiste')) {
    mixes.add('CALMER')
    touch.add('Lavanda Touch')
    difusor.add('Lavanda')
    if (temResposta('sono', 'profundo', 'não descansa')) difusor.add('Serenity')
  }

  // FOCO — Vetiver Touch apenas para ADOLESCENTE com demandas específicas
  if (temResposta('foco', 'disperso', 'hiperativ', 'TDAH', 'dificuldade', 'procrastin', 'queda', 'não termina')) {
    mixes.add('THINKER')
    if (faixa === 'ADOLESCENTE' && demandasPermitiamVetiver(demandas)) {
      touch.add('Vetiver Touch')
    }
  }

  // EMOCIONAL
  if (temResposta('emocional', 'birra', 'explosão', 'raiva', 'instável', 'superestimul', 'triste', 'isolado', 'sem limites', 'não cooperativ')) {
    mixes.add('STEADY')
    difusor.add('Petitgrain')
  }
  if (temResposta('emocional', 'ansioso', 'medo', 'inseguro', 'baixa autoestima', 'tímido', 'desânimo', 'estômago')) {
    mixes.add('BRAVE')
  }
  if (temResposta('emocional', 'chora', 'sensível', 'humor')) {
    difusor.add('Tangerina')
  }

  // SAÚDE FÍSICA
  if (temResposta('saude_fisica', 'respiratório', 'bronquite', 'asma', 'rinite', 'tosse', 'congestão')) {
    mixes.add('TAMER')
    touch.add('Breathe Touch')
    difusor.add('Lavanda')
  }
  if (temResposta('saude_fisica', 'digestivo', 'gases', 'constipação', 'dor de barriga', 'náusea', 'cólica', 'refluxo')) {
    mixes.add('TAMER')
  }
  if (temResposta('saude_fisica', 'dor de cabeça', 'dores musculares', 'dores de crescimento', 'tensão')) {
    mixes.add('RESCUER')
  }
  if (temResposta('saude_fisica', 'pele', 'assadura', 'irritação', 'eczema', 'acne')) {
    mixes.add('TAMER')
  }

  // IMUNIDADE
  if (temResposta('imunidade', 'frequência', 'adoece', 'demora', 'infecções', 'alergias', 'recuperar')) {
    mixes.add('STRONGER')
  }

  // HUMOR (bebê)
  if (temResposta('humor', 'agitado', 'choroso', 'irritado', 'difícil de acalmar', 'assustado')) {
    mixes.add('CALMER')
    mixes.add('STEADY')
  }

  // COMPORTAMENTO (pré-escolar)
  if (temResposta('comportamento', 'agitado', 'hiperativ', 'tímido', 'apegado', 'desobediente', 'dificuldade de adaptação')) {
    mixes.add('STEADY')
    if (temResposta('comportamento', 'tímido', 'adaptação')) mixes.add('BRAVE')
  }

  // SOCIALIZAÇÃO (criança e adolescente)
  if (temResposta('socializacao', 'tímido', 'conflitos', 'retraído', 'agressivo', 'baixa autoestima', 'isolado')) {
    mixes.add('BRAVE')
    mixes.add('STEADY')
  }

  // DEMANDAS ESPECIAIS
  if (demandas.some(d => d.includes('TDAH') || d.includes('Autismo') || d.includes('DDA') || d.includes('Hiperatividade') || d.includes('Déficit de atenção'))) {
    mixes.add('THINKER')
    // Vetiver Touch: APENAS adolescentes com demandas específicas
    if (faixa === 'ADOLESCENTE' && demandasPermitiamVetiver(demandas)) {
      touch.add('Vetiver Touch')
    }
    if (!demandas.some(d => d.includes('Autismo'))) mixes.add('STEADY')
  }
  if (demandas.some(d => d.includes('Ansiedade'))) {
    mixes.add('BRAVE')
    mixes.add('CALMER')
  }
  if (demandas.some(d => d.includes('Depressão'))) {
    mixes.add('BRAVE')
    mixes.add('STEADY')
    difusor.add('Laranja-selvagem')
  }
  if (demandas.some(d => d.includes('Doença autoimune'))) {
    mixes.add('STRONGER')
  }
  if (demandas.some(d => d.includes('Alergias'))) {
    mixes.add('STRONGER')
    touch.add('Breathe Touch')
  }

  // PRIORIDADES
  if (prioridades.includes('Qualidade do sono')) { mixes.add('CALMER'); difusor.add('Lavanda') }
  if (prioridades.includes('Foco e concentração')) {
    mixes.add('THINKER')
    // Vetiver Touch via prioridade: apenas ADOLESCENTE com demandas específicas
    if (faixa === 'ADOLESCENTE' && demandasPermitiamVetiver(demandas)) {
      touch.add('Vetiver Touch')
    }
  }
  if (prioridades.includes('Equilíbrio emocional') || prioridades.includes('Equilíbrio emocional e calma')) mixes.add('STEADY')
  if (prioridades.includes('Imunidade e proteção')) mixes.add('STRONGER')
  if (prioridades.includes('Saúde respiratória')) { mixes.add('TAMER'); touch.add('Breathe Touch') }
  if (prioridades.includes('Digestão e conforto abdominal')) mixes.add('TAMER')
  if (prioridades.includes('Alívio de dores') || prioridades.includes('Alívio de dores e tensão')) mixes.add('RESCUER')
  if (prioridades.includes('Coragem e autoconfiança') || prioridades.includes('Autoestima e autoconfiança')) mixes.add('BRAVE')
  if (prioridades.includes('Calma e tranquilidade') || prioridades.includes('Comportamento e limites')) mixes.add('STEADY')
  if (prioridades.includes('Conforto digestivo') || prioridades.includes('Conforto e alívio de desconfortos')) mixes.add('TAMER')
  if (prioridades.includes('Saúde da pele')) mixes.add('TAMER')

  // Óleos touch exclusivos para adolescentes
  if (faixa === 'ADOLESCENTE') {
    if (demandas.some(d => d.includes('Ansiedade')) || mixes.has('BRAVE')) {
      touch.add('Adaptiv Touch')
    }
    if (mixes.has('STRONGER')) touch.add('On Guard Touch')
    if (mixes.has('THINKER') || mixes.has('RESCUER')) touch.add('Hortelã-Pimenta Touch')
    if (mixes.has('TAMER')) touch.add('Zengest Touch')
  }

  // Difusores extras
  if (mixes.has('CALMER') || mixes.has('STEADY') || mixes.has('BRAVE')) {
    difusor.add('Laranja-selvagem')
  }
  if (mixes.has('STEADY')) difusor.add('Tangerina')

  return {
    mixes: Array.from(mixes),
    touch: Array.from(touch),
    difusor: Array.from(difusor),
  }
}

// ═══════════════════════════════════════
// HELPER: IDENTIFICAR FAIXA ETÁRIA
// ═══════════════════════════════════════

function identificarFaixa(idadeTexto: string): FaixaEtaria {
  const texto = idadeTexto.toLowerCase().trim()
  const mesesMatch = texto.match(/(\d+)\s*mes/)
  if (mesesMatch) {
    const meses = parseInt(mesesMatch[1])
    if (meses <= 24) return 'BEBE'
    if (meses <= 72) return 'PRE_ESCOLAR'
    if (meses <= 144) return 'CRIANCA'
    return 'ADOLESCENTE'
  }
  const anosMatch = texto.match(/(\d+)\s*ano/)
  if (anosMatch) {
    const anos = parseInt(anosMatch[1])
    if (anos <= 2) return 'BEBE'
    if (anos <= 6) return 'PRE_ESCOLAR'
    if (anos <= 12) return 'CRIANCA'
    if (anos <= 17) return 'ADOLESCENTE'
    return 'ADOLESCENTE'
  }
  const numMatch = texto.match(/^(\d+)$/)
  if (numMatch) {
    const n = parseInt(numMatch[1])
    if (n <= 2) return 'BEBE'
    if (n <= 6) return 'PRE_ESCOLAR'
    if (n <= 12) return 'CRIANCA'
    if (n <= 17) return 'ADOLESCENTE'
  }
  return ''
}

// ═══════════════════════════════════════
// NOMES DAS FAIXAS
// ═══════════════════════════════════════

const NOME_FAIXA: Record<string, string> = {
  BEBE: 'Bebê (0 a 2 anos)',
  PRE_ESCOLAR: 'Pré-escolar (3 a 6 anos)',
  CRIANCA: 'Criança (7 a 12 anos)',
  ADOLESCENTE: 'Adolescente (13 a 17 anos)',
}

// ═══════════════════════════════════════
// DADOS DOS MIXES (ícone + indicação)
// ═══════════════════════════════════════

const MIXES_INFO: Record<string, { emoji: string; nomePort: string; indicacao: string }> = {
  CALMER:   { emoji: '🌙', nomePort: 'Mix para Serenidade',  indicacao: 'Sono tranquilo e calma' },
  THINKER:  { emoji: '🧠', nomePort: 'Mix para Foco',        indicacao: 'Concentração e aprendizagem' },
  BRAVE:    { emoji: '🦁', nomePort: 'Mix para Coragem',     indicacao: 'Autoconfiança e superação' },
  STEADY:   { emoji: '⚖️', nomePort: 'Mix para Equilíbrio',  indicacao: 'Humor estável e bem-estar' },
  STRONGER: { emoji: '🛡️', nomePort: 'Mix Protetor',         indicacao: 'Imunidade e proteção' },
  TAMER:    { emoji: '🌬️', nomePort: 'Mix Digestivo',        indicacao: 'Conforto respiratório e digestivo' },
  RESCUER:  { emoji: '💙', nomePort: 'Mix para Conforto',    indicacao: 'Alívio de tensão e dores' },
}

// ═══════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════

export default function DiagnosticoKids() {
  const [etapa, setEtapa] = useState<'intro' | 'dados' | 'perguntas' | 'demanda' | 'prioridade' | 'pergunta_extra1' | 'pergunta_extra2' | 'resultado'>('intro')
  const [dados, setDados] = useState<DadosBasicos>({
    nomeMae: '',
    whatsapp: '',
    paisOpcao: PAISES[0],
    nomeKid: '',
    idadeKid: '',
  })
  const [faixa, setFaixa] = useState<FaixaEtaria>('')
  const [perguntaAtual, setPerguntaAtual] = useState(0)
  const [respostas, setRespostas] = useState<Record<string, string[]>>({})
  const [demandas, setDemandas] = useState<string[]>([])
  const [prioridades, setPrioridades] = useState<string[]>([])
  const [conheceOleos, setConheceOleos] = useState('')
  const [cadastradaDoterra, setCadadastradaDoterra] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [leadId, setLeadId] = useState<string | null>(null)
  const [resultado, setResultado] = useState<{ mixes: string[]; touch: string[]; difusor: string[] } | null>(null)
  const [progresso, setProgresso] = useState(0)
  const [exibirOleosResultadoKids, setExibirOleosResultadoKids] = useState(false)

  // Detectar consultora da URL
  const urlParams = new URLSearchParams(window.location.search)
  const consultora = urlParams.get('c') || 'geral'

  useEffect(() => {
    if (!consultora || consultora === 'geral') {
      setExibirOleosResultadoKids(false)
      return
    }
    let ativo = true
    lumi.entities.consultoras.list({
      filter: { usuario: consultora },
      limit: 1,
    }).then((resp) => {
      if (!ativo) return
      const consultoraObj = resp?.list?.[0]
      setExibirOleosResultadoKids(
        consultoraObj?.exibirOleosResultadoKids === true ||
        consultoraObj?.exibirOleosResultadoKids === 'true'
      )
    }).catch((err) => {
      console.error('[kids] erro ao carregar config da consultora', err)
      if (ativo) setExibirOleosResultadoKids(false)
    })
    return () => { ativo = false }
  }, [consultora])

  // Perguntas baseadas na faixa
  const perguntas = faixa === 'BEBE' ? perguntasBebe
    : faixa === 'PRE_ESCOLAR' ? perguntasPreEscolar
    : faixa === 'CRIANCA' ? perguntasCrianca
    : faixa === 'ADOLESCENTE' ? perguntasAdolescente
    : []

  const demandasFaixa = faixa === 'BEBE' ? demandasBebe
    : faixa === 'PRE_ESCOLAR' ? demandasPreEscolar
    : faixa === 'CRIANCA' ? demandasCrianca
    : demandasAdolescente

  const prioridadesFaixa = faixa === 'BEBE' ? prioridadesBebe
    : faixa === 'PRE_ESCOLAR' ? prioridadesPreEscolar
    : faixa === 'CRIANCA' ? prioridadesCrianca
    : prioridadesAdolescente

  useEffect(() => {
    if (etapa === 'intro') setProgresso(0)
    else if (etapa === 'dados') setProgresso(5)
    else if (etapa === 'perguntas') setProgresso(Math.round(10 + (perguntaAtual / Math.max(perguntas.length, 1)) * 55))
    else if (etapa === 'demanda') setProgresso(70)
    else if (etapa === 'prioridade') setProgresso(80)
    else if (etapa === 'pergunta_extra1') setProgresso(88)
    else if (etapa === 'pergunta_extra2') setProgresso(94)
    else if (etapa === 'resultado') setProgresso(100)
  }, [etapa, perguntaAtual, perguntas.length])

  const avancarDados = async (e: React.FormEvent) => {
    e.preventDefault()
    const { nomeMae, whatsapp, nomeKid, idadeKid, paisOpcao } = dados
    const val = validarNumero(whatsapp, paisOpcao)
    if (!val.valido) { toast.error(val.mensagem); return }
    if (!nomeMae.trim() || !nomeKid.trim() || !idadeKid.trim()) {
      toast.error('Preencha todos os campos')
      return
    }
    const f = identificarFaixa(idadeKid)
    if (!f) {
      toast.error('Não foi possível identificar a faixa etária. Tente escrever como "3 anos" ou "8 meses".')
      return
    }
    setFaixa(f)

    setSalvando(true)
    try {
      const whatsappFormatado = paisOpcao.codigo ? paisOpcao.codigo.replace('+', '') + whatsapp : whatsapp
      const now = new Date().toISOString()
      const res = await lumi.entities.leads_kids.create({
        nomeMae: nomeMae.trim(),
        whatsapp: whatsappFormatado,
        nomeKid: nomeKid.trim(),
        idadeKid: idadeKid.trim(),
        faixaEtaria: f,
        pais: paisOpcao.nomeSalvo,
        consultora,
        respostas: {},
        demandaEspecial: [],
        prioridades: [],
        mixesIndicados: [],
        oleosTouch: [],
        oleosDifusor: [],
        status: 'Novo lead',
        data_hora: now,
        creator: 'sistema',
        createdAt: now,
        updatedAt: now,
      })
      setLeadId((res as any)?._id || null)
    } catch (err) {
      console.error('[kids] erro ao salvar lead inicial', err)
    } finally {
      setSalvando(false)
    }
    setEtapa('perguntas')
    setPerguntaAtual(0)
  }

  // Seleciona/deseleciona opção (até 2 nas perguntas multiplas)
  const toggleOpcao = (opcao: string) => {
    const pergAtual = perguntas[perguntaAtual]
    const isMultipla = pergAtual.multipla
    setRespostas(prev => {
      const atual = prev[pergAtual.id] || []
      if (atual.includes(opcao)) {
        return { ...prev, [pergAtual.id]: atual.filter(x => x !== opcao) }
      }
      if (isMultipla && atual.length >= 2) {
        toast.error('Selecione no máximo 2 opções')
        return prev
      }
      return { ...prev, [pergAtual.id]: [...atual, opcao] }
    })
  }

  const confirmarPergunta = () => {
    const pergAtual = perguntas[perguntaAtual]
    const resp = respostas[pergAtual.id] || []
    if (resp.length === 0) {
      toast.error('Selecione pelo menos uma opção')
      return
    }
    if (perguntaAtual < perguntas.length - 1) {
      setPerguntaAtual(p => p + 1)
    } else {
      setEtapa('demanda')
    }
  }

  const toggleDemanda = (d: string) => {
    if (d === 'Nenhuma das anteriores') {
      setDemandas(['Nenhuma das anteriores'])
      return
    }
    setDemandas(prev => {
      const sem = prev.filter(x => x !== 'Nenhuma das anteriores')
      return sem.includes(d) ? sem.filter(x => x !== d) : [...sem, d]
    })
  }

  const avancarDemanda = () => {
    if (demandas.length === 0) {
      toast.error('Selecione pelo menos uma opção')
      return
    }
    setEtapa('prioridade')
  }

  const togglePrioridade = (p: string) => {
    setPrioridades(prev => {
      if (prev.includes(p)) return prev.filter(x => x !== p)
      if (prev.length >= 3) { toast.error('Escolha no máximo 3 prioridades'); return prev }
      return [...prev, p]
    })
  }

  const avancarPrioridade = () => {
    if (prioridades.length === 0) {
      toast.error('Selecione pelo menos uma prioridade')
      return
    }
    setEtapa('pergunta_extra1')
  }

  const responderExtra1 = (opcao: string) => {
    setConheceOleos(opcao)
    setEtapa('pergunta_extra2')
  }

  const responderExtra2 = async (opcao: string) => {
    setCadadastradaDoterra(opcao)
    const calc = calcularMixes(respostas, demandas, prioridades, faixa)
    setResultado(calc)
    setEtapa('resultado')

    if (leadId) {
      try {
        await lumi.entities.leads_kids.update(leadId, {
          respostas,
          demandaEspecial: demandas,
          prioridades,
          mixesIndicados: calc.mixes,
          oleosTouch: calc.touch,
          oleosDifusor: calc.difusor,
          conheceOleos: conheceOleos,
          cadastradaDoterra: opcao,
          updatedAt: new Date().toISOString(),
        })
      } catch (err) {
        console.error('[kids] erro ao atualizar lead', err)
      }
    }
  }

  const voltar = () => {
    if (etapa === 'perguntas') {
      if (perguntaAtual > 0) setPerguntaAtual(p => p - 1)
      else setEtapa('dados')
    } else if (etapa === 'demanda') {
      setEtapa('perguntas')
      setPerguntaAtual(perguntas.length - 1)
    } else if (etapa === 'prioridade') {
      setEtapa('demanda')
    } else if (etapa === 'pergunta_extra1') {
      setEtapa('prioridade')
    } else if (etapa === 'pergunta_extra2') {
      setEtapa('pergunta_extra1')
    } else if (etapa === 'resultado') {
      setEtapa('pergunta_extra2')
    }
  }

  const reiniciar = () => {
    setEtapa('intro')
    setDados({ nomeMae: '', whatsapp: '', paisOpcao: PAISES[0], nomeKid: '', idadeKid: '' })
    setFaixa('')
    setPerguntaAtual(0)
    setRespostas({})
    setDemandas([])
    setPrioridades([])
    setConheceOleos('')
    setCadadastradaDoterra('')
    setResultado(null)
    setLeadId(null)
  }

  const nomeKidDisplay = dados.nomeKid.split(' ')[0] || 'sua criança'

  // ─── RENDER ───────────────────────────────

  return (
    <div className="min-h-screen bg-[#F8F4EF] font-dm-sans">
      {/* Barra de progresso */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1.5 bg-[#EDE0D4]">
        <div
          className="h-full bg-[#2D6A4F] transition-all duration-500"
          style={{ width: `${progresso}%` }}
        />
        {etapa !== 'intro' && etapa !== 'dados' && (
          <div className="absolute right-3 top-2.5 text-xs text-[#5F5E5A] font-medium">{progresso}%</div>
        )}
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-10 pb-16">

        {/* ETAPA: INTRODUÇÃO */}
        {etapa === 'intro' && (
          <div className="animate-fade-in-up">
            {/* Bloco de apresentação da consultora */}
            <BlocoApresentacaoConsultora
              consultora={consultora}
              modo="intro"
              mensagemFixa="Que incrível que você chegou aqui! Após preencher o diagnóstico entrarei em contato pelo WhatsApp para te orientar, esclarecer suas dúvidas e te ajudar a cuidar do seu filho(a) de forma mais natural."
            />

            <div className="text-center">
              <span className="inline-block bg-[#A8DFC4] text-[#2D6A4F] text-sm font-medium px-4 py-1.5 rounded-full mb-6">
                Diagnóstico Personalizado
              </span>

              <h1 className="text-4xl md:text-5xl font-cormorant font-semibold text-[#2C2C2A] leading-tight mb-3">
                Diagnóstico de Saúde e Emoções para Crianças e Adolescentes
              </h1>

              <p className="text-[#5F5E5A] text-lg leading-relaxed max-w-xl mx-auto mb-10">
                Descubra quais óleos essenciais são indicados para o bem-estar do seu filho(a).
                Responda com sinceridade. Cada pergunta revela um caminho para cuidar de quem você ama.
              </p>

              <div className="max-w-md mx-auto space-y-4 mb-10 text-left">
                {[
                  'Perguntas personalizadas por faixa etária',
                  'Resultado personalizado ao final',
                  'Leva cerca de 5 minutos',
                  '100% gratuito',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-[#A8DFC4] flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-[#2D6A4F]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span className="text-[#2C2C2A]">{item}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setEtapa('dados')}
                className="bg-[#2D6A4F] text-white px-8 py-4 rounded-full font-medium text-lg hover:bg-[#245A42] transition-all shadow-lg hover:shadow-xl"
              >
                Começar o diagnóstico →
              </button>

              <p className="text-xs text-[#5F5E5A] mt-4">
                🔒 Seus dados são confidenciais e não serão compartilhados.
              </p>
            </div>
          </div>
        )}

        {/* ETAPA: DADOS BÁSICOS */}
        {etapa === 'dados' && (
          <div className="animate-fade-in-up">
            <div className="text-center mb-10">
              <span className="inline-block bg-[#D8F3DC] text-[#2D6A4F] text-sm font-medium px-4 py-1.5 rounded-full mb-4">
                🌿 Diagnóstico Kids
              </span>
              <h1 className="text-4xl md:text-5xl font-cormorant font-semibold text-[#2C2C2A] leading-tight mb-4">
                Diagnóstico de Saúde e Emoções para Crianças
              </h1>
              <p className="text-[#5F5E5A] text-lg leading-relaxed">
                Descubra quais óleos essenciais são indicados para o seu filho(a). Responda com sinceridade — cada pergunta revela um caminho.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-8">
              <p className="text-sm italic text-[#B08968] mb-2">Antes de começar</p>
              <h2 className="text-2xl font-cormorant font-semibold text-[#2C2C2A] mb-6">
                Me conta um pouco sobre você e seu filho(a)
              </h2>
              <form onSubmit={avancarDados} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#5F5E5A] mb-2">
                    Seu nome completo (mãe/responsável) *
                  </label>
                  <input
                    type="text"
                    required
                    value={dados.nomeMae}
                    onChange={e => setDados(d => ({ ...d, nomeMae: e.target.value }))}
                    placeholder="Ex: Ana Maria Silva"
                    className="w-full px-4 py-3 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none text-[#2C2C2A]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#5F5E5A] mb-2">
                    Seu WhatsApp *
                  </label>
                  <PhoneInput
                    value={dados.whatsapp}
                    onChange={(num, pais) => setDados(d => ({ ...d, whatsapp: num, paisOpcao: pais }))}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#5F5E5A] mb-2">
                    Nome da criança *
                  </label>
                  <input
                    type="text"
                    required
                    value={dados.nomeKid}
                    onChange={e => setDados(d => ({ ...d, nomeKid: e.target.value }))}
                    placeholder="Ex: Sophia"
                    className="w-full px-4 py-3 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none text-[#2C2C2A]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#5F5E5A] mb-2">
                    Idade da criança *
                  </label>
                  <input
                    type="text"
                    required
                    value={dados.idadeKid}
                    onChange={e => setDados(d => ({ ...d, idadeKid: e.target.value }))}
                    placeholder="Ex: 6 meses, 3 anos, 10 anos"
                    className="w-full px-4 py-3 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none text-[#2C2C2A]"
                  />
                  <p className="text-xs text-[#5F5E5A] mt-1.5">O sistema identifica automaticamente a faixa etária</p>
                </div>

                <button
                  type="submit"
                  disabled={salvando}
                  className="w-full bg-[#2D6A4F] text-white py-4 rounded-full font-medium text-lg hover:bg-[#245A42] transition-all disabled:opacity-60 mt-2"
                >
                  {salvando ? 'Aguarde...' : 'Começar o diagnóstico →'}
                </button>
              </form>

              <p className="text-center text-xs text-[#5F5E5A] mt-4">
                🔒 Seus dados são confidenciais e não serão compartilhados.
              </p>
            </div>
          </div>
        )}

        {/* ETAPA: PERGUNTAS — seleção de até 2 opções */}
        {etapa === 'perguntas' && perguntas.length > 0 && (() => {
          const pergAtual = perguntas[perguntaAtual]
          const respostaAtual = respostas[pergAtual.id] || []
          return (
            <div className="animate-fade-in-up">
              <div className="flex items-center gap-3 mb-8">
                <button onClick={voltar} className="p-2 rounded-full hover:bg-[#EDE0D4] transition-colors text-[#5F5E5A]">
                  ← Voltar
                </button>
                <div className="flex-1 text-center">
                  <span className="text-sm text-[#5F5E5A]">
                    Pergunta {perguntaAtual + 1} de {perguntas.length}
                  </span>
                  <div className="text-xs text-[#A8DFC4] mt-0.5">
                    {NOME_FAIXA[faixa]}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-md p-8">
                <p className="text-xs text-[#B08968] uppercase tracking-widest mb-3 font-medium">
                  {pergAtual.titulo}
                </p>
                <h2 className="text-2xl font-cormorant font-semibold text-[#2C2C2A] mb-2 leading-snug">
                  {pergAtual.pergunta.replace('[nome]', nomeKidDisplay)}
                </h2>
                <p className="text-sm text-[#5F5E5A] mb-6">
                  Selecione até 2 opções que mais descrevem a situação atual.
                </p>

                <div className="space-y-3 mb-6">
                  {pergAtual.opcoes.map((opcao, i) => {
                    const selecionada = respostaAtual.includes(opcao)
                    return (
                      <button
                        key={i}
                        onClick={() => toggleOpcao(opcao)}
                        className={`w-full text-left px-5 py-4 border-2 rounded-xl transition-all font-medium flex items-center gap-3 ${
                          selecionada
                            ? 'border-[#2D6A4F] bg-[#D8F3DC] text-[#2D6A4F]'
                            : 'border-[#EDE0D4] text-[#2C2C2A] hover:border-[#2D6A4F] hover:bg-[#F0FAF4]'
                        }`}
                      >
                        <span className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          selecionada ? 'border-[#2D6A4F] bg-[#2D6A4F]' : 'border-[#EDE0D4]'
                        }`}>
                          {selecionada && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </span>
                        {opcao}
                      </button>
                    )
                  })}
                </div>

                <button
                  onClick={confirmarPergunta}
                  disabled={respostaAtual.length === 0}
                  className="w-full bg-[#2D6A4F] text-white py-4 rounded-full font-medium text-lg hover:bg-[#245A42] transition-all disabled:opacity-40"
                >
                  Continuar →
                </button>
              </div>
            </div>
          )
        })()}

        {/* ETAPA: DEMANDAS ESPECIAIS */}
        {etapa === 'demanda' && (
          <div className="animate-fade-in-up">
            <div className="flex items-center gap-3 mb-8">
              <button onClick={voltar} className="p-2 rounded-full hover:bg-[#EDE0D4] transition-colors text-[#5F5E5A]">
                ← Voltar
              </button>
              <div className="flex-1 text-center">
                <span className="text-sm text-[#5F5E5A]">Etapa especial</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-8">
              <p className="text-xs text-[#B08968] uppercase tracking-widest mb-3 font-medium">
                Demandas especiais
              </p>
              <h2 className="text-2xl font-cormorant font-semibold text-[#2C2C2A] mb-3 leading-snug">
                {nomeKidDisplay} tem algum diagnóstico ou demanda especial?
              </h2>
              <p className="text-sm text-[#5F5E5A] mb-6">Pode marcar mais de uma.</p>

              <div className="space-y-2 mb-8">
                {demandasFaixa.map((d, i) => (
                  <button
                    key={i}
                    onClick={() => toggleDemanda(d)}
                    className={`w-full text-left px-5 py-3.5 border-2 rounded-xl transition-all text-sm font-medium flex items-center gap-3 ${
                      demandas.includes(d)
                        ? 'border-[#2D6A4F] bg-[#D8F3DC] text-[#2D6A4F]'
                        : 'border-[#EDE0D4] text-[#2C2C2A] hover:border-[#A8DFC4]'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      demandas.includes(d) ? 'border-[#2D6A4F] bg-[#2D6A4F]' : 'border-[#EDE0D4]'
                    }`}>
                      {demandas.includes(d) && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                    </span>
                    {d}
                  </button>
                ))}
              </div>

              <button
                onClick={avancarDemanda}
                className="w-full bg-[#2D6A4F] text-white py-4 rounded-full font-medium text-lg hover:bg-[#245A42] transition-all"
              >
                Continuar →
              </button>
            </div>
          </div>
        )}

        {/* ETAPA: PRIORIDADES */}
        {etapa === 'prioridade' && (
          <div className="animate-fade-in-up">
            <div className="flex items-center gap-3 mb-8">
              <button onClick={voltar} className="p-2 rounded-full hover:bg-[#EDE0D4] transition-colors text-[#5F5E5A]">
                ← Voltar
              </button>
              <div className="flex-1 text-center">
                <span className="text-sm text-[#5F5E5A]">Última etapa</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-8">
              <p className="text-xs text-[#B08968] uppercase tracking-widest mb-3 font-medium">
                Prioridade
              </p>
              <h2 className="text-2xl font-cormorant font-semibold text-[#2C2C2A] mb-3 leading-snug">
                O que você mais quer melhorar na saúde e bem-estar de {nomeKidDisplay}?
              </h2>
              <p className="text-sm text-[#5F5E5A] mb-6">Escolha até 3 opções. ({prioridades.length}/3 selecionadas)</p>

              <div className="space-y-2 mb-8">
                {prioridadesFaixa.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => togglePrioridade(p)}
                    className={`w-full text-left px-5 py-3.5 border-2 rounded-xl transition-all text-sm font-medium flex items-center gap-3 ${
                      prioridades.includes(p)
                        ? 'border-[#2D6A4F] bg-[#D8F3DC] text-[#2D6A4F]'
                        : 'border-[#EDE0D4] text-[#2C2C2A] hover:border-[#A8DFC4]'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      prioridades.includes(p) ? 'border-[#2D6A4F] bg-[#2D6A4F]' : 'border-[#EDE0D4]'
                    }`}>
                      {prioridades.includes(p) && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                    </span>
                    {p}
                  </button>
                ))}
              </div>

              <button
                onClick={avancarPrioridade}
                disabled={prioridades.length === 0}
                className="w-full bg-[#2D6A4F] text-white py-4 rounded-full font-medium text-lg hover:bg-[#245A42] transition-all disabled:opacity-50"
              >
                Continuar →
              </button>
            </div>
          </div>
        )}

        {/* ETAPA: PERGUNTA EXTRA 1 — Conhece óleos? */}
        {etapa === 'pergunta_extra1' && (
          <div className="animate-fade-in-up">
            <div className="flex items-center gap-3 mb-8">
              <button onClick={voltar} className="p-2 rounded-full hover:bg-[#EDE0D4] transition-colors text-[#5F5E5A]">
                ← Voltar
              </button>
              <div className="flex-1 text-center">
                <span className="text-sm text-[#5F5E5A]">Quase lá — pergunta 1 de 2</span>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-md p-8">
              <p className="text-xs text-[#B08968] uppercase tracking-widest mb-3 font-medium">Sobre os óleos essenciais</p>
              <h2 className="text-2xl font-cormorant font-semibold text-[#2C2C2A] mb-8 leading-snug">
                Você já conhece os óleos essenciais?
              </h2>
              <div className="space-y-3">
                {[
                  'Nunca ouvi falar',
                  'Já ouvi falar mas nunca usei',
                  'Já usei outros óleos essenciais',
                  'Já usei óleos dōTERRA',
                ].map((opcao, i) => (
                  <button
                    key={i}
                    onClick={() => responderExtra1(opcao)}
                    className="w-full text-left px-5 py-4 border-2 border-[#EDE0D4] rounded-xl hover:border-[#2D6A4F] hover:bg-[#F0FAF4] transition-all text-[#2C2C2A] font-medium"
                  >
                    {opcao}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ETAPA: PERGUNTA EXTRA 2 — Cadastrada dōTERRA? */}
        {etapa === 'pergunta_extra2' && (
          <div className="animate-fade-in-up">
            <div className="flex items-center gap-3 mb-8">
              <button onClick={voltar} className="p-2 rounded-full hover:bg-[#EDE0D4] transition-colors text-[#5F5E5A]">
                ← Voltar
              </button>
              <div className="flex-1 text-center">
                <span className="text-sm text-[#5F5E5A]">Quase lá — pergunta 2 de 2</span>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-md p-8">
              <p className="text-xs text-[#B08968] uppercase tracking-widest mb-3 font-medium">Sobre a dōTERRA</p>
              <h2 className="text-2xl font-cormorant font-semibold text-[#2C2C2A] mb-8 leading-snug">
                Você é cadastrada na dōTERRA?
              </h2>
              <div className="space-y-3">
                {[
                  'Não sou cadastrada',
                  'Sou cadastrada mas não estou ativa',
                  'Sou cadastrada e estou ativa',
                ].map((opcao, i) => (
                  <button
                    key={i}
                    onClick={() => responderExtra2(opcao)}
                    className="w-full text-left px-5 py-4 border-2 border-[#EDE0D4] rounded-xl hover:border-[#2D6A4F] hover:bg-[#F0FAF4] transition-all text-[#2C2C2A] font-medium"
                  >
                    {opcao}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ETAPA: RESULTADO */}
        {etapa === 'resultado' && resultado && (
          <ResultadoKids
            resultado={resultado}
            dados={dados}
            nomeKid={dados.nomeKid}
            faixa={faixa}
            prioridades={prioridades}
            consultora={consultora}
            cadastradaDoterra={cadastradaDoterra}
            exibirOleos={exibirOleosResultadoKids}
            onReiniciar={reiniciar}
          />
        )}
      </div>

      <style>{`
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up { animation: fade-in-up 0.4s ease-out; }
        @keyframes fade-out {
          from { opacity: 1; max-height: 500px; }
          to { opacity: 0; max-height: 0; }
        }
        .animate-fade-out { animation: fade-out 0.4s ease-out forwards; }
      `}</style>
    </div>
  )
}

// ═══════════════════════════════════════
// COMPONENTE DE RESULTADO PÚBLICO
// 7 blocos: título, mixes, segurança,
// aguardar mensagem, diagnóstico adulto,
// compartilhar, pergunta final (condicional)
// SEM valores, PV ou opções de compra.
// ═══════════════════════════════════════

function ResultadoKids({
  resultado,
  dados,
  nomeKid,
  consultora,
  cadastradaDoterra,
  exibirOleos,
  onReiniciar,
}: {
  resultado: { mixes: string[]; touch: string[]; difusor: string[] }
  dados: DadosBasicos
  nomeKid: string
  faixa: FaixaEtaria
  prioridades: string[]
  consultora: string
  cadastradaDoterra: string
  exibirOleos: boolean
  onReiniciar: () => void
}) {
  const [bloco7Visivel, setBloco7Visivel] = useState(true)
  const [bloco7Sumindo, setBloco7Sumindo] = useState(false)

  const nomeDisplay = nomeKid.split(' ')[0] || 'sua criança'
  const nomeMaeDisplay = dados.nomeMae.split(' ')[0] || 'você'
  const totalMixes = resultado.mixes.length

  // Link diagnóstico adulto: ?c= da consultora ou monica como fallback
  const consultoraCta = consultora && consultora !== 'geral' ? consultora : 'monica'
  const linkAdulto = `https://www.descubraseusoleos.com.br/?c=${consultoraCta}`
  const linkKids = `https://www.descubraseusoleos.com.br/kids?c=${consultoraCta}`

  // Mensagem de compartilhamento WhatsApp
  const msgCompartilhar = encodeURIComponent(
    `Oi! Fiz um diagnóstico gratuito de saúde e bem-estar para ${nomeDisplay} e foi incrível!\n\nDescubra quais óleos essenciais são indicados para o seu filho também. É rápido e gratuito:\n\n${linkKids}`
  )

  // Bloco 7: exibir apenas se NÃO for "Sou cadastrada e estou ativa"
  const exibirBloco7 = cadastradaDoterra !== 'Sou cadastrada e estou ativa' && bloco7Visivel

  const recusarBloco7 = () => {
    setBloco7Sumindo(true)
    setTimeout(() => setBloco7Visivel(false), 400)
  }

  return (
    <div className="animate-fade-in-up space-y-6">

      {/* BLOCO 1 — Título (apenas quando os óleos são exibidos) */}
      {exibirOleos ? (
      <div className="bg-white rounded-2xl shadow-md px-8 pt-10 pb-6 text-center">
        <h1 className="text-3xl md:text-4xl font-cormorant font-semibold text-[#2C2C2A] leading-snug mb-4">
          Isso é o que o corpo e as emoções de{' '}
          <span className="text-[#2D6A4F]">{nomeDisplay}</span>{' '}
          estão pedindo 🌿
        </h1>
        <p className="text-[#5F5E5A] text-lg leading-relaxed">
          De acordo com tudo que você respondeu,{' '}
          <strong className="text-[#2C2C2A]">{totalMixes} {totalMixes === 1 ? 'óleo essencial' : 'óleos essenciais'}</strong>{' '}
          podem trazer mudanças reais para{' '}
          <strong className="text-[#2D6A4F]">{nomeDisplay}</strong>:
        </p>
      </div>
      ) : null}

      {/* BLOCO 2 — Mixes indicados */}
      {exibirOleos && resultado.mixes.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-md p-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {resultado.mixes.map((mix, i) => {
              const info = MIXES_INFO[mix] || { emoji: '🌿', indicacao: '' }
              return (
                <div
                  key={i}
                  className="flex flex-col items-center text-center p-4 rounded-xl border border-[#EDE0D4] bg-[#F8F4EF]"
                >
                  <span className="text-3xl mb-2">{info.emoji}</span>
                  <span className="text-sm font-semibold text-[#2D6A4F] leading-snug mb-1">{info.nomePort}</span>
                  <span className="text-xs text-[#5F5E5A] leading-snug">{info.indicacao}</span>
                </div>
              )
            })}
          </div>
        </div>
      ) : null}

      {/* BLOCO 3 — Segurança */}
      {exibirOleos ? (
      <div className="bg-white rounded-2xl shadow-md p-8">
        <div className="flex items-start gap-3 mb-4">
          <span className="text-2xl">💡</span>
          <h2 className="text-xl font-cormorant font-semibold text-[#2C2C2A] leading-snug">
            Seguro para crianças e adolescentes
          </h2>
        </div>
        <div className="text-[#5F5E5A] leading-relaxed space-y-3 text-sm">
          <p>
            Essas misturas de óleos essenciais são especialmente formuladas para crianças e adolescentes.
            Já vêm diluídas em óleo de coco fracionado e prontas para usar diretamente na pele sem precisar diluir.
          </p>
          <p>
            São livres de aditivos químicos agressivos e testados para uso seguro em todas as faixas etárias de 0 a 17 anos.
          </p>
          <p>
            Onde consta <em>Mix</em>, trata-se de uma combinação de óleos essenciais em um único frasco,
            formulada para atuar de forma mais completa e potente — cuidando do desconforto físico e emocional ao mesmo tempo.
          </p>
          <p>
            Os óleos essenciais não substituem tratamentos médicos, mas podem ser uma ferramenta diária de cuidado,
            equilíbrio e transformação para <strong className="text-[#2D6A4F]">{nomeDisplay}</strong> e para toda a família.
          </p>
        </div>
      </div>
      ) : (
      <>
      <div className="bg-white rounded-2xl shadow-md px-8 pt-10 pb-6 text-center">
        <h1 className="text-3xl md:text-4xl font-cormorant font-semibold text-[#2C2C2A] leading-snug">
          Diagnóstico de{' '}
          <span className="text-[#2D6A4F]">{nomeDisplay}</span>
        </h1>
      </div>
      <div className="bg-white rounded-2xl shadow-md p-8">
        <p className="text-[#5F5E5A] text-base leading-relaxed">
          Em breve você receberá uma mensagem minha no WhatsApp com orientações detalhadas sobre como cuidar da saúde e bem-estar de{' '}
          <strong className="text-[#2D6A4F]">{nomeDisplay}</strong> de forma natural com os óleos essenciais.
        </p>
      </div>
      <BlocoApresentacaoConsultora consultora={consultora} modo="identidade" />
      </>
      )}

      {exibirOleos ? (
      <BlocoApresentacaoConsultora
        consultora={consultora}
        modo="resultado"
        textoEncerramento="🌿 Você já descobriu quais óleos essenciais são indicados para o seu filho(a). Agora é só aguardar que em breve você receberá uma mensagem minha no WhatsApp com orientações mais detalhadas de como usar cada um, qual protocolo seguir e como adquirir.

Cuide-se com carinho.
Uma gota muda tudo. ✨"
      />
      ) : null}

      {/* BLOCO 5 — Diagnóstico adulto */}
      <div className="bg-white rounded-2xl shadow-md p-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="flex-1 h-px bg-[#A8DFC4]" />
          <span className="text-xl">🌿</span>
          <div className="flex-1 h-px bg-[#A8DFC4]" />
        </div>
        <div className="text-center mt-5">
          <h2 className="text-2xl font-cormorant font-semibold text-[#2C2C2A] mb-3">
            E você, {nomeMaeDisplay}, como está se sentindo?
          </h2>
          <p className="text-[#5F5E5A] text-sm leading-relaxed mb-5">
            Que tal descobrir quais óleos essenciais o <strong>SEU</strong> corpo e mente precisam?<br />
            O <strong>Diagnóstico de Saúde e Emoções</strong> é gratuito e leva menos de 3 minutos.
          </p>
          <a
            href={linkAdulto}
            className="inline-block bg-[#2D6A4F] text-white px-8 py-4 rounded-full font-medium text-base hover:bg-[#245A42] transition-all"
          >
            Fazer meu diagnóstico
          </a>
        </div>
      </div>

      {/* BLOCO 6 — Compartilhar */}
      <div className="text-center">
        <a
          href={`https://wa.me/?text=${msgCompartilhar}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-full font-medium text-base hover:bg-[#1ebe57] transition-all"
        >
          💚 Indique para outra mãe
        </a>
      </div>

      {/* BLOCO 7 — Pergunta final (condicional) */}
      {exibirBloco7 && (
        <div
          className={`bg-white rounded-2xl shadow-md p-8 text-center border border-[#EDE0D4] ${bloco7Sumindo ? 'animate-fade-out' : 'animate-fade-in-up'}`}
        >
          <p className="text-2xl mb-3">✨</p>
          <h2 className="text-xl font-cormorant font-semibold text-[#2C2C2A] mb-4 leading-snug">
            Uma pergunta antes de você ir...
          </h2>
          <p className="text-[#5F5E5A] text-sm leading-relaxed mb-6">
            Você sabia que outras mães também poderiam se beneficiar desse diagnóstico?<br /><br />
            Se você tivesse acesso de forma <strong className="text-[#2D6A4F]">GRATUITA</strong> a uma estratégia completa,
            uma ferramenta profissional e uma capacitação, você gostaria de trabalhar com os óleos essenciais e ter uma renda extra?
          </p>
          <div className="space-y-3">
            <a
              href={`https://www.descubraseusoleos.com.br/capacitacao?indicado_por=${consultoraCta}`}
              className="block w-full bg-[#2D6A4F] text-white py-4 rounded-full font-medium text-base hover:bg-[#245A42] transition-all"
            >
              Sim, quero saber mais!
            </a>
            <button
              onClick={recusarBloco7}
              className="block w-full text-[#5F5E5A] text-sm py-2 hover:text-[#2C2C2A] transition-colors"
            >
              Não por enquanto
            </button>
          </div>
        </div>
      )}

      {/* Botão reiniciar */}
      <div className="text-center pt-2">
        <button onClick={onReiniciar} className="text-sm text-[#5F5E5A] hover:text-[#2D6A4F] underline">
          Refazer o diagnóstico
        </button>
      </div>
    </div>
  )
}
